import React, { useMemo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Button from 'srcReact/components/common2/Button'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'
import Modal from 'srcReact/components/common2/Modal'
import NotFound from 'srcReact/components/common2/NotFound'
import ReactMenu from 'srcReact/components/common2/ReactMenu'
import ReactTable from 'srcReact/components/common2/ReactTable'
import SearchTextBox from 'srcReact/components/common2/SearchTextBox'
import Icon from 'srcReact/components/icons/Icon'
import { formatLastUpdateDate, getDateFormat3 } from 'srcReact/helpers/datetimeHelper'
import { getCurrentOrgId } from 'srcReact/helpers/organizationHelper'
import { getIconNameFromExtension } from 'srcReact/helpers/utils'
import DesktopHeader from '../DesktopHeader'
import DeleteDocumentModal from './DeleteDocumentModal'
import DocumentList from './DocumentList'
import styles from './styles.module.scss'
import UploadDocumentModal from './UploadDocumentModal'

function OrgDocuments(props) {
  const { t } = useTranslation()
  const [orgId, setOrgId] = useState(0)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [fileForAction, setFileForAction] = useState(null)
  const [isLink, setIsLink] = useState(false)

  const dispatch = useDispatch()
  const {
    common: { isMobile, isNative },
    organizationSettings: {
      orgFiles: { list, stats, loading, error }
    }
  } = useSelector((state) => state)

  useEffect(() => {
    const currentOrgId = getCurrentOrgId()
    setOrgId(currentOrgId)
  }, [])

  const handleUpdate = (file) => {
    setFileForAction(file)
    setIsUploadModalOpen(true)
    setIsLink(file?.file_url?.length > 0)
  }

  const handleDelete = (file) => {
    setFileForAction(file)
    setIsDeleteModalOpen(true)
    setIsLink(file?.file_url?.length > 0)
  }

  const columns = useMemo(
    () => [
      {
        Header: '',
        id: 'icon',
        accessor: (row, idx) => {
          if (row?.file_url?.length > 0) {
            return <Icon name='link' size='large24' />
          } else if (row?.file_info?.name) {
            const extention = row.file_info.name.split('.').pop().toLowerCase()
            const iconName = getIconNameFromExtension(extention || '')
            return iconName ? <Icon name={iconName} size='large24' /> : null
          } else return null
        },
        className: styles.rowIcon
      },
      {
        Header: t('global.documentName'),
        id: 'name',
        accessor: (row, idx) => {
          if (row?.file_url?.length > 0) {
            return (
              <a className={styles.fileName} href={row.file_url} target='_blank'>
                {row.file_url}
              </a>
            )
          } else if (row?.file_info?.name) {
            return (
              <button className={styles.fileName} onClick={() => handleDownload(row)}>
                {row.file_info.name}
              </button>
            )
          } else return null
        },
        className: styles.rowFileName
      },
      {
        Header: t('global.lastUpdated'),
        id: 'date',
        accessor: (row, idx) => (
          <div className={styles.lastUpdated}>{formatLastUpdateDate(row?.updatedAt, getDateFormat3())}</div>
        ),
        className: styles.rowLastUpdated
      },
      {
        Header: '',
        id: 'options',
        accessor: (row, idx) => (
          <ReactMenu options={options(row)} menuButtonCSS={styles.menuButtonCSS} menuCSS={styles.menuCSS} />
        )
      }
    ],
    []
  )

  const closeModal = () => {
    setIsUploadModalOpen(false)
  }
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false)
  }

  const uploadNewDocument = () => {
    setFileForAction(null)
    setIsUploadModalOpen(true)
  }
  return (
    <div className={styles.documentWrapper}>
      {!isMobile && (
        <DesktopHeader
          title={t('organizationSettings.documents.title')}
          subtitle={t('organizationSettings.documents.subtitle')}
        />
      )}
      <Button
        text={t('global.uploadDoc')}
        customStyle={styles.uploadBtn}
        style='tertiary'
        handleClick={uploadNewDocument}
      />
      <DocumentList showActions={true} orgId={orgId} handleDelete={handleDelete} handleUpdate={handleUpdate} />

      <Modal isOpen={isUploadModalOpen} title={t('global.uploadDoc')} handleClose={closeModal}>
        <UploadDocumentModal orgId={orgId} isLink={isLink} closeModal={closeModal} fileToUpdate={fileForAction} />
      </Modal>
      <Modal
        isOpen={isDeleteModalOpen}
        title={t('organizationSettings.documents.deleteFileConfirm', {
          type: isLink ? t('global.link').toLowerCase() : t('global.document').toLowerCase()
        })}
        handleClose={closeDeleteModal}>
        <DeleteDocumentModal orgId={orgId} closeModal={closeDeleteModal} fileToDelete={fileForAction} />
      </Modal>
    </div>
  )
}

export default OrgDocuments
